/**
 * Veluna Capture Guard — the single arbitration authority for intrusive
 * email capture.
 *
 * POLICY ONLY. It renders nothing, owns no UI, and stores no subscriber data.
 * Each capture engine keeps its own triggers, storage, cooldowns and submit
 * handler; this only answers one question: "may anything interrupt right now?"
 *
 * Replaces the reference to veluna-luna-decision-engine.js, which 404'd on
 * every page since the 2026-08-16 asset cleanup. That file defined
 * window.LunaDecisionEngine (three pure helper functions) and NOTHING in the
 * theme ever called it — restoring it would have restored a no-op. Retired.
 *
 * Rules implemented (deliberately only these):
 *   1. One intrusive overlay at a time.
 *   2. Purchase configuration on screen suppresses capture.
 *   3. A purchase action just taken suppresses capture.
 *   4. An email already captured suppresses further prompts.
 * Existing dismissal/frequency memory is untouched and still authoritative —
 * this guard can only ever say "no", never "yes".
 */
(function (w, d) {
  'use strict';
  if (w.VelunaCaptureGuard) return;

  /* Purchase-configuration blocks on the PDP and the quiz result page. */
  var COMMERCE_SEL = [
    '[data-veluna-purchase-type]',
    '[data-mr-purchase-type]',
    '.recovery-cream-hero__velunasub',
    '[data-velunasub-schedule]',
    '[data-velunasub-onetime]',
    '[data-mr-schedule]'
  ].join(',');

  /* Controls that mean "this visitor is transacting". */
  var PURCHASE_SEL = '[data-veluna-atc],#recovery-cream-add-to-cart,#mr-cta,' +
                     '#mr-v2-sticky-btn,#mr-cta-dup,#mr-v2-cta2-btn,' +
                     '[name="checkout"],a[href*="/checkout"]';

  /* Overlays owned by the capture engines, used for the one-at-a-time check.
     The unified-popup and the article/blog popup were previously invisible to
     this list, which is why the homepage could render two full-viewport capture
     overlays at once at every tested width. */
  var OVERLAY_SEL = '.vpm__overlay.is-open,.vqp__overlay.is-open,.mr-sheet.is-open,' +
                    '.luna-popup,[data-luna-popup],' +
                    '.unified-popup.unified-popup--visible,' +
                    '.veluna-fs-overlay.is-open,[data-veluna-article-popup].is-open,' +
                    '.veluna-exit-intent-modal--visible,.veluna-fs-popup.is-open';

  /* Arbitration priority when two engines become eligible in the same tick.
     Higher wins. Rule: a PAGE-SPECIFIC capture outranks the generic site-wide
     one, because it is the offer the visitor's current context earned.
     Equal priority falls back to first-to-acquire, which the owner lock already
     enforces. */
  var PRIORITY = {
    'pdp-mystery': 30,
    'quiz-results-exit': 30,
    'article-capture': 20,
    'pdf-capture': 10,
    'unified-popup': 5
  };
  function rank(id) { return PRIORITY[id] != null ? PRIORITY[id] : 10; }

  /* PRECEDENCE, not preemption.
     A page-specific engine announces itself at load. The generic site-wide
     capture then stands down on that page instead of racing it to the guard.
     This is what makes the outcome deterministic: whichever engine happens to
     trigger first no longer decides which lane owns the page. */
  var pageSpecific = [];
  /* Engines may load before OR after this file, so the queue is read live on
     every query rather than drained once at init. Script order cannot change
     the outcome. */
  function allPageSpecific() {
    var out = pageSpecific.slice();
    try {
      (w.__vlnPageSpecific || []).forEach(function (id) {
        if (id && out.indexOf(id) === -1) out.push(id);
      });
    } catch (_) {}
    return out;
  }

  var PURCHASE_WINDOW_MS = 8000;

  var commerceOnScreen = false;
  var lastPurchaseIntent = 0;
  var currentOwner = null;

  function onScreen(el) {
    if (!el) return false;
    var cs = w.getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) < 0.05) return false;
    var r = el.getBoundingClientRect();
    return r.bottom > 0 && r.top < (w.innerHeight || 0) &&
           r.right > 0 && r.left < (w.innerWidth || 0) && r.width > 1 && r.height > 1;
  }

  /* Rule 2 — IntersectionObserver, not a timer: the page tells us where the
     visitor is. Re-scanned on DOM changes because the quiz result page builds
     its commerce block client-side after the recommendation resolves. */
  var observer = null;
  var watched = [];
  function watchCommerce() {
    if (!w.IntersectionObserver) return;
    var nodes = [].slice.call(d.querySelectorAll(COMMERCE_SEL));
    if (!nodes.length) return;
    if (!observer) {
      observer = new w.IntersectionObserver(function () {
        commerceOnScreen = watched.some(onScreen);
      }, { threshold: 0.15 });
    }
    nodes.forEach(function (n) {
      if (watched.indexOf(n) === -1) { watched.push(n); observer.observe(n); }
    });
    commerceOnScreen = watched.some(onScreen);
  }

  /* Rule 3 — capture purchase intent in the capture phase so it registers
     even when a handler stops propagation. */
  d.addEventListener('click', function (e) {
    try {
      if (e.target && e.target.closest && e.target.closest(PURCHASE_SEL)) {
        lastPurchaseIntent = Date.now();
      }
    } catch (_) {}
  }, true);

  /* Rule 4 — read the engines' OWN existing keys. No new storage is created,
     so dismissal/frequency memory keeps working exactly as before. */
  function alreadyCaptured() {
    try {
      if (localStorage.getItem('veluna_pdf_captured')) return true;
    } catch (_) {}
    try {
      if (typeof w.hasEmailCaptured === 'function' && w.hasEmailCaptured()) return true;
    } catch (_) {}
    return false;
  }

  /* Rule 1 — is any intrusive overlay already on screen? */
  function overlayOpen() {
    var nodes = d.querySelectorAll(OVERLAY_SEL);
    for (var i = 0; i < nodes.length; i++) if (onScreen(nodes[i])) return true;
    return false;
  }

  /* Priority of the highest-ranked overlay currently on screen. */
  function highestOpenRank() {
    var nodes = d.querySelectorAll(OVERLAY_SEL), best = 0;
    for (var i = 0; i < nodes.length; i++) {
      if (!onScreen(nodes[i])) continue;
      var owner = nodes[i].getAttribute('data-capture-id') || '';
      var r = owner ? rank(owner) : 10;
      if (r > best) best = r;
    }
    return best;
  }

  function suppressReason(id) {
    if (alreadyCaptured()) return 'already_captured';
    /* Self-healing: an engine that closed its overlay without telling us must
       not be able to deadlock every other surface for the rest of the session. */
    if (currentOwner && !overlayOpen()) currentOwner = null;
    if (currentOwner && currentOwner !== id) return 'overlay_open:' + currentOwner;
    /* A lower-priority engine may not open on top of a higher-priority one that
       is already on screen, even if that engine never claimed ownership. */
    if (!currentOwner && overlayOpen() && rank(id) < highestOpenRank()) {
      return 'lower_priority_than_open_overlay';
    }
    if (overlayOpen()) return 'overlay_on_screen';
    if (commerceOnScreen) return 'commerce_configuration_visible';
    if (Date.now() - lastPurchaseIntent < PURCHASE_WINDOW_MS) return 'purchase_in_progress';
    return null;
  }

  w.VelunaCaptureGuard = {
    /* Called at load by an engine that is specific to this page type. */
    registerPageSpecific: function (id) {
      if (id && pageSpecific.indexOf(id) === -1) pageSpecific.push(id);
    },
    /* True when this page has a dedicated capture, so the generic one yields. */
    hasPageSpecific: function (exceptId) {
      var all = allPageSpecific();
      for (var i = 0; i < all.length; i++) {
        if (all[i] !== exceptId) return true;
      }
      return false;
    },
    pageSpecific: function () { return allPageSpecific(); },
    /* The only method the engines need. Returns false = do not interrupt. */
    mayShow: function (id) { return suppressReason(id) === null; },
    reason: function (id) { return suppressReason(id); },
    notifyOpen: function (id) { currentOwner = id || 'unknown'; },
    notifyClosed: function (id) { if (!id || currentOwner === id) currentOwner = null; },
    commerceVisible: function () { return commerceOnScreen; },
    /* Exposed for QA/diagnostics only. */
    state: function () {
      return {
        commerceOnScreen: commerceOnScreen,
        purchaseIntentAgoMs: lastPurchaseIntent ? Date.now() - lastPurchaseIntent : null,
        currentOwner: currentOwner,
        overlayOpen: overlayOpen(),
        alreadyCaptured: alreadyCaptured(),
        pageSpecific: allPageSpecific()
      };
    }
  };

  function start() {
    watchCommerce();
    if (w.MutationObserver && d.body) {
      var debounce;
      new w.MutationObserver(function () {
        clearTimeout(debounce);
        debounce = setTimeout(watchCommerce, 250);
      }).observe(d.body, { childList: true, subtree: true });
    }
    w.addEventListener('scroll', function () {
      commerceOnScreen = watched.some(onScreen);
    }, { passive: true });
  }

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', start);
  else start();
})(window, document);
